import { confirmNavigation } from '../shared/navigationGuard';
import {
  heydayPagePaths,
  type HeydayPage,
} from './heydayPageManifest';

type SearchInput = string | URLSearchParams | undefined;

type LocationInput = Pick<Location, 'pathname' | 'search' | 'hash'>;

const canonicalPageEntries = Object.entries(heydayPagePaths) as Array<[HeydayPage, string]>;

const pageByPath = new Map(canonicalPageEntries.map(([page, path]) => [path, page]));
if (pageByPath.size !== canonicalPageEntries.length) throw new Error('Heyday canonical page paths must be unique.');

const heydayLocationChangeEvent = 'heyday:location-change';
const bulkOperations = new Set([
  'EMPLOYEE_HIRE',
  'EMPLOYEE_IMPORT',
  'EMPLOYEE_REHIRE',
  'STAFFING_CHANGE',
  'WORKER_LIFECYCLE_CORRECTION',
  'TIME_OFF_CORRECTION',
  'TIME_OFF_GRANT',
  'TIME_OFF_ADJUSTMENT',
]);
const workflowRequestStatuses = new Set(['DRAFT', 'NEEDS_CHANGES', 'IN_PROGRESS', 'AWAITING_EFFECTIVE', 'EXECUTION_UNVERIFIED', 'COMPLETED', 'DENIED', 'CANCELLED', 'CLOSED']);

function normalizedPathname(pathname: string) {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized || '/';
}

function normalizedSearch(search: SearchInput) {
  if (!search) return '';
  const value = typeof search === 'string' ? search.replace(/^\?/, '') : search.toString();
  return value ? `?${value}` : '';
}

const canonicalTenantSettingsViews: Partial<Record<HeydayPage, ReadonlySet<string>>> = {
  'admin-company-info': new Set(['name']),
  'admin-affiliates': new Set(['legal']),
  'admin-company-seals': new Set(['seal']),
  'admin-reference-data': new Set([
    'locations',
    'work-spaces',
    'job-families',
    'job-profiles',
    'job-levels',
    'job-types',
    'employee-number',
  ]),
};

function canonicalTenantSettingsSearch(page: HeydayPage, search: string) {
  const parameters = new URLSearchParams(search);
  const requestedView = parameters.get('view') ?? '';
  const requestedCanonicalView = canonicalTenantSettingsViews[page]?.has(requestedView)
    ? requestedView
    : undefined;
  const company = parameters.get('company');
  const detailRequiresCompany = (page === 'admin-affiliates' && requestedCanonicalView === 'legal')
    || (page === 'admin-company-seals' && requestedCanonicalView === 'seal');
  const canonicalView = detailRequiresCompany && !company ? undefined : requestedCanonicalView;
  const canonical = new URLSearchParams();
  if (canonicalView) canonical.set('view', canonicalView);
  const companyApplies = page === 'admin-company-info'
    || (page === 'admin-affiliates' && canonicalView === 'legal')
    || (page === 'admin-company-seals' && canonicalView === 'seal');
  if (company && companyApplies) canonical.set('company', company);
  return normalizedSearch(canonical);
}

function canonicalPageSearch(page: HeydayPage, search: string) {
  if (page === 'organization') {
    const parameters = new URLSearchParams(search);
    const planId = parameters.get('planId')?.trim();
    if (planId) {
      const canonical = new URLSearchParams({ planId, view: parameters.get('view') === 'general' ? 'general' : 'studio' });
      const worker = parameters.get('worker')?.trim();
      if (planId === 'new' && worker) {
        canonical.set('worker', worker);
        const organization = parameters.get('organization')?.trim();
        if (organization) canonical.set('organization', organization);
        const effectiveDate = parameters.get('effectiveDate')?.trim() ?? '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) canonical.set('effectiveDate', effectiveDate);
      }
      return normalizedSearch(canonical);
    }
    const studio = parameters.get('studio')?.trim();
    return studio ? normalizedSearch(new URLSearchParams({ studio })) : '';
  }
  if (page === 'my-absence') {
    return new URLSearchParams(search).get('action') === 'request' ? '?action=request' : '';
  }
  if (page === 'directory') {
    const parameters = new URLSearchParams(search);
    const canonical = new URLSearchParams();
    const worker = parameters.get('worker')?.trim();
    const organization = worker ? undefined : parameters.get('organization')?.trim();
    if (parameters.get('view') === 'chart' || organization) canonical.set('view', 'chart');
    const query = parameters.get('search');
    if (query?.trim()) canonical.set('search', query);
    if (worker) canonical.set('worker', worker);
    else if (organization) canonical.set('organization', organization);
    return normalizedSearch(canonical);
  }
  if (page === 'admin-getting-started') {
    const parameters = new URLSearchParams(search);
    const asOf = parameters.get('asOf');
    const item = parameters.get('item');
    const canonical = new URLSearchParams();
    if (asOf && /^\d{4}-\d{2}-\d{2}$/.test(asOf)) {
      const parsed = new Date(`${asOf}T00:00:00Z`);
      if (Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === asOf) canonical.set('asOf', asOf);
    }
    if (item && ['company', 'employee-number', 'reference-data', 'organization-people', 'administrators', 'absence'].includes(item)) canonical.set('item', item);
    return normalizedSearch(canonical);
  }
  if (page === 'absence-settings') {
    const parameters = new URLSearchParams(search);
    const view = parameters.get('view');
    const canonical = new URLSearchParams();
    if (view === 'calendars' || view === 'setup') canonical.set('view', view);
    else if (view === 'types' || view === 'plans' || view === 'overview') canonical.set('view', 'types');
    const typeId = parameters.get('type')?.trim();
    if (typeId && (!view || view === 'types')) {
      canonical.set('view', 'types');
      canonical.set('type', typeId);
    }
    return normalizedSearch(canonical);
  }
  if (page === 'lifecycle') {
    const parameters = new URLSearchParams(search);
    if (parameters.get('view') === 'templates') return '?view=templates';
    const worker = parameters.get('worker')?.trim();
    if (parameters.get('action') !== 'onboarding' || !worker) return '';
    const canonical = new URLSearchParams({ action: 'onboarding', worker });
    const targetOn = parameters.get('targetOn');
    if (targetOn && /^\d{4}-\d{2}-\d{2}$/.test(targetOn)) {
      const parsed = new Date(`${targetOn}T00:00:00Z`);
      if (Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === targetOn) canonical.set('targetOn', targetOn);
    }
    return normalizedSearch(canonical);
  }
  if (page === 'my-workflows') {
    const parameters = new URLSearchParams(search);
    const draftID = parameters.get('draft')?.trim();
    const composer = parameters.get('composer') === 'fullscreen' ? 'fullscreen' : undefined;
    if (draftID || composer) {
      const canonical = new URLSearchParams();
      if (draftID) canonical.set('draft', draftID);
      if (composer) canonical.set('composer', composer);
      return normalizedSearch(canonical);
    }
    return '';
  }
  if (page === 'my-tasks' || page === 'inbox') {
    const parameters = new URLSearchParams(search);
    const canonical = new URLSearchParams();
    for (const key of ['task', 'search', 'processType']) if (parameters.get(key)?.trim()) canonical.set(key, parameters.get(key)!);
    if (parameters.get('view') === 'history') canonical.set('view', 'history');
    const offset = Number(parameters.get('offset'));
    if (Number.isSafeInteger(offset) && offset > 0) canonical.set('offset', String(offset));
    if (parameters.get('task')?.trim() && parameters.get('presentation') === 'fullscreen') canonical.set('presentation', 'fullscreen');
    return normalizedSearch(canonical);
  }
  if (page === 'my-workflow-requests') {
    const parameters = new URLSearchParams(search);
    const status = parameters.get('status');
    const requestID = parameters.get('request')?.trim();
    const canonical = new URLSearchParams();
    if (status && workflowRequestStatuses.has(status)) canonical.set('status', status);
    if (parameters.get('search')?.trim()) canonical.set('search', parameters.get('search')!);
    if (parameters.get('templateId')?.trim()) canonical.set('templateId', parameters.get('templateId')!);
    for (const key of ['dateFrom', 'dateTo']) {
      const value = parameters.get(key);
      if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const parsed = new Date(`${value}T00:00:00Z`);
        if (Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value) canonical.set(key, value);
      }
    }
    if (requestID) canonical.set('request', requestID);
    if (requestID && parameters.get('presentation') === 'fullscreen') canonical.set('presentation', 'fullscreen');
    return normalizedSearch(canonical);
  }
  if (page === 'workflow-requests') {
    const parameters = new URLSearchParams(search);
    const canonical = new URLSearchParams();
    for (const key of ['search', 'templateId']) if (parameters.get(key)?.trim()) canonical.set(key, parameters.get(key)!);
    const status = parameters.get('status');
    if (status && ['DRAFT', 'NEEDS_CHANGES', 'IN_PROGRESS', 'AWAITING_EFFECTIVE', 'EXECUTION_UNVERIFIED', 'COMPLETED', 'DENIED', 'CANCELLED'].includes(status)) canonical.set('status', status);
    for (const key of ['dateFrom', 'dateTo']) {
      const value = parameters.get(key);
      if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const parsed = new Date(`${value}T00:00:00Z`);
        if (Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value) canonical.set(key, value);
      }
    }
    const requestID = parameters.get('request')?.trim();
    if (requestID) canonical.set('request', requestID);
    if (requestID && parameters.get('presentation') === 'fullscreen') canonical.set('presentation', 'fullscreen');
    return normalizedSearch(canonical);
  }
  if (page === 'bulk') {
    const parameters = new URLSearchParams(search);
    const operation = parameters.get('operation');
    const job = parameters.get('job')?.trim();
    const canonical = new URLSearchParams();
    if (operation && bulkOperations.has(operation)) canonical.set('operation', operation);
    if (job) canonical.set('job', job);
    const source = parameters.get('source');
    if (source === 'people' || source === 'staffing') canonical.set('source', source);
    return normalizedSearch(canonical);
  }
  if (page === 'admin-integrations') {
    const parameters = new URLSearchParams(search);
    const view = parameters.get('view');
    if (view === 'directory' || view === 'messaging') return normalizedSearch(new URLSearchParams({ view }));
    const provider = parameters.get('provider');
    return provider === 'google-workspace' || provider === 'slack' || provider === 'absence-sync'
      ? normalizedSearch(new URLSearchParams({ provider }))
      : '';
  }
  if (['admin-company-info', 'admin-affiliates', 'admin-company-seals', 'admin-reference-data', 'admin-appearance'].includes(page)) {
    return canonicalTenantSettingsSearch(page, search);
  }
  return search;
}

export function heydayPath(page: HeydayPage, search?: SearchInput) {
  const query = normalizedSearch(search);
  const canonicalQuery = canonicalPageSearch(page, query);
  return `${heydayPagePaths[page]}${canonicalQuery}`;
}

export function heydayPersonPath(workerID: string, search?: SearchInput) {
  const normalizedWorkerID = workerID.trim();
  if (!normalizedWorkerID) return heydayPath('people', search);
  return `${heydayPagePaths.people}/${encodeURIComponent(normalizedWorkerID)}${normalizedSearch(search)}`;
}

export function peopleWorkerIDFromPathname(pathname: string) {
  const normalized = normalizedPathname(pathname);
  const detailPrefix = `${heydayPagePaths.people}/`;
  if (!normalized.startsWith(detailPrefix)) return undefined;
  const encodedWorkerID = normalized.slice(detailPrefix.length);
  if (!encodedWorkerID || encodedWorkerID.includes('/')) return undefined;
  try {
    return decodeURIComponent(encodedWorkerID);
  } catch {
    return undefined;
  }
}

export function pageFromPathname(pathname: string): HeydayPage | undefined {
  const normalized = normalizedPathname(pathname);
  return pageByPath.get(normalized) ?? (peopleWorkerIDFromPathname(normalized) ? 'people' : undefined);
}

export function resolveHeydayLocation(location: LocationInput): { page: HeydayPage; canonicalURL: string; replace: boolean } {
  const pathname = normalizedPathname(location.pathname);

  const workerID = peopleWorkerIDFromPathname(pathname);
  if (workerID) {
    const canonicalURL = `${heydayPersonPath(workerID, location.search)}${location.hash}`;
    return { page: 'people', canonicalURL, replace: canonicalURL !== `${location.pathname}${location.search}${location.hash}` };
  }

  const page = pageFromPathname(pathname);
  if (page) {
    const canonicalURL = `${heydayPath(page, location.search)}${location.hash}`;
    return { page, canonicalURL, replace: canonicalURL !== `${location.pathname}${location.search}${location.hash}` };
  }

  const canonicalURL = heydayPath('my-home', pathname === '/' ? location.search : undefined);
  return { page: 'my-home', canonicalURL, replace: canonicalURL !== `${location.pathname}${location.search}${location.hash}` };
}

export function canonicalizeCurrentHeydayLocation() {
  const route = resolveHeydayLocation(window.location);
  if (route.replace) window.history.replaceState(window.history.state, '', route.canonicalURL);
  return route.page;
}

export function navigateToHeydayPage(page: HeydayPage, search?: SearchInput, options: { replace?: boolean } = {}) {
  const path = heydayPath(page, search);
  return navigateToHeydayPath(path, options, page === 'directory' && !options.replace);
}

export function navigateToHeydayPerson(workerID: string, search?: SearchInput, options: { replace?: boolean } = {}) {
  return navigateToHeydayPath(heydayPersonPath(workerID, search), options);
}

function navigateToHeydayPath(path: string, options: { replace?: boolean }, notifyOnSamePath = false) {
  if (`${window.location.pathname}${window.location.search}` === path && !window.location.hash) {
    // 같은 디렉터리 검색으로 다시 진입해도 페이지의 로컬 필터는 새 목적지에 맞춥니다.
    if (notifyOnSamePath) window.dispatchEvent(new Event(heydayLocationChangeEvent));
    return true;
  }
  if (!confirmNavigation()) return false;
  trackCurrentLocation();
  const index = acceptedIndex + (options.replace ? 0 : 1);
  window.history[options.replace ? 'replaceState' : 'pushState']({ ...window.history.state, [navigationIndexKey]: index }, '', path);
  trackCurrentLocation();
  window.dispatchEvent(new Event(heydayLocationChangeEvent));
  return true;
}

const navigationIndexKey = 'heydayNavigationIndex';
const locationListeners = new Set<() => void>();
let acceptedIndex = 0;
let acceptedURL = '';
let acceptedState: unknown;
let restoringLocation = false;

function trackCurrentLocation() {
  const state = window.history.state;
  acceptedIndex = typeof state?.[navigationIndexKey] === 'number' ? state[navigationIndexKey] : acceptedIndex;
  acceptedURL = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  acceptedState = { ...state, [navigationIndexKey]: acceptedIndex };
  if (state?.[navigationIndexKey] !== acceptedIndex) window.history.replaceState(acceptedState, '', acceptedURL);
}

function notifyLocationChange() {
  trackCurrentLocation();
  locationListeners.forEach((listener) => listener());
}

function handleHistoryNavigation() {
  if (restoringLocation) { restoringLocation = false; return; }
  if (!confirmNavigation()) {
    const targetIndex = window.history.state?.[navigationIndexKey];
    if (typeof targetIndex === 'number' && targetIndex !== acceptedIndex) {
      restoringLocation = true;
      window.history.go(acceptedIndex - targetIndex);
    } else {
      window.history.replaceState(acceptedState, '', acceptedURL);
    }
    return;
  }
  notifyLocationChange();
}

export function subscribeToHeydayLocation(listener: () => void) {
  if (!locationListeners.size) {
    trackCurrentLocation();
    window.addEventListener('popstate', handleHistoryNavigation);
    window.addEventListener(heydayLocationChangeEvent, notifyLocationChange);
  }
  locationListeners.add(listener);
  return () => {
    locationListeners.delete(listener);
    if (!locationListeners.size) {
      window.removeEventListener('popstate', handleHistoryNavigation);
      window.removeEventListener(heydayLocationChangeEvent, notifyLocationChange);
    }
  };
}

export function currentHeydayReturnTo() {
  const route = resolveHeydayLocation(window.location);
  const canonical = new URL(route.canonicalURL, window.location.origin);
  return `${canonical.pathname}${canonical.search}`;
}
